import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Paper,
  Alert,
  CircularProgress,
} from '@mui/material';
import { LocalizationProvider, DateCalendar } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useLunchMenu, useCreateLunchMenu } from '../../hooks/useLunch';
import { Save as SaveIcon } from '@mui/icons-material';
import { format } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { LUNCH, DATE_FORMATS } from '../../constants';

const LunchMenu: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [menuText, setMenuText] = useState('');
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const dateString = selectedDate.toISOString().split('T')[0];
  const { data: menu, isLoading } = useLunchMenu(dateString);
  const createMenu = useCreateLunchMenu();

  // Update menuText when menu data changes
  useEffect(() => {
    if (menu?.rawText) {
      setMenuText(menu.rawText);
    } else {
      setMenuText('');
    }
  }, [menu]);

  const handleSubmit = async () => {
    if (!menuText.trim()) {
      setError(LUNCH.MESSAGES.ENTER_MENU_TEXT);
      setSuccess('');
      return;
    }

    try {
      await createMenu.mutateAsync({
        date: dateString,
        rawText: menuText
      });

      setSuccess(LUNCH.MESSAGES.MENU_SAVED);
      setError('');
    } catch (err) {
      setError(LUNCH.MESSAGES.MENU_SAVE_ERROR);
      setSuccess('');
    }
  };

  return (
    <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
      <Box sx={{ flex: '1 1 350px', minWidth: 300 }}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Select Date
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {LUNCH.MESSAGES.SELECT_DATE_MENU}
            </Typography>
            <LocalizationProvider 
              dateAdapter={AdapterDateFns}
              adapterLocale={{...enUS, options: { ...enUS.options, weekStartsOn: 1 }}}
            >
              <DateCalendar
                value={selectedDate}
                onChange={(newDate) => newDate && setSelectedDate(newDate)}
              />
            </LocalizationProvider>
          </CardContent>
        </Card>
      </Box>

      <Box sx={{ flex: '2 1 600px', minWidth: 300 }}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Menu for {format(selectedDate, DATE_FORMATS.DISPLAY)}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Enter the menu text. Each line with a number will be parsed as a menu item.
            </Typography>

            {success && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
                {success}
              </Alert>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}

            <TextField
              fullWidth
              multiline
              rows={15}
              value={menuText}
              onChange={(e) => setMenuText(e.target.value)}
              placeholder="Example:&#10;Domaća supa150&#10;Pileća corba200&#10;Meni1(Roštilj kobasica +kupus700)"
              sx={{ mb: 2, fontFamily: 'monospace' }}
            />

            <Button
              variant="contained"
              fullWidth
              onClick={handleSubmit}
              disabled={createMenu.isPending || isLoading}
              startIcon={createMenu.isPending ? <CircularProgress size={20} /> : <SaveIcon />}
            >
              Save Menu
            </Button>

            {menu && menu.items.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Parsed Items ({menu.items.length}):
                </Typography>
                <Paper sx={{ maxHeight: 300, overflow: 'auto' }}>
                  {menu.items.map((item: any) => (
                    <Box
                      key={item.id}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        p: 1.5,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        '&:last-child': { borderBottom: 'none' }
                      }}
                    >
                      <Typography variant="body2">{item.name}</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {item.price} RSD
                      </Typography>
                    </Box>
                  ))}
                </Paper>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default LunchMenu;
